import { ESRI_GEOCODE_SERVICE_URL } from '@rsos/maps/interface/reverseGeocode/helpers';
import {
  GOOGLE_PLACE_SOURCE,
  googleForwardGeocode,
} from '@rsos/maps/interface/rsosGeocoder/googleForwardGeocode';
import {
  isRsosGeocoderEnabled,
  RSOS_PLACE_SOURCE,
  rsosForwardGeocode,
} from '@rsos/maps/interface/rsosGeocoder/rsosGeocoder';
import { getAddressFromMagicKey } from '@rsos/reduxDataStore/src/features/gisSearch/helpers';
import { MAX_POI } from './GlobalMapSearch.constants';
import type { PlaceCandidate } from './GlobalMapSearch.types';
import {
  normalizePlaceCoords,
  PlaceResultDedupe,
} from './GlobalMapSearch.utils';

function normalizeServiceUrl(url?: string): string {
  return (url || '').trim().toLowerCase().replace(/\/+$/, '');
}

const GLOBAL_PLACE_SOURCES = new Set([
  normalizeServiceUrl(ESRI_GEOCODE_SERVICE_URL),
  RSOS_PLACE_SOURCE,
  GOOGLE_PLACE_SOURCE,
]);

export function isGlobalPlaceService(url?: string): boolean {
  return GLOBAL_PLACE_SOURCES.has(normalizeServiceUrl(url));
}

export function isGlobalPlace(candidate: PlaceCandidate): boolean {
  return isGlobalPlaceService(candidate._serviceUrl);
}

export type AbortableThunkResult = Promise<Record<string, unknown>> & {
  abort?: () => void;
  unsubscribe?: () => void;
};

export type ThunkDispatch = (action: unknown) => AbortableThunkResult;

export interface SuggestEntry {
  text: string;
  magicKey: string;
  _serviceUrl?: string;
  _token?: string | null;
}

/**
 * RTK Query dispatch results expose `abort()` for in-flight requests. Bridge
 * the search orchestrator's AbortSignal into those requests so a newer
 * keystroke cancels stale suggestion work instead of only ignoring the result.
 */
export function abortThunkOnSignal(
  request: AbortableThunkResult,
  signal: AbortSignal,
): () => void {
  if (signal.aborted) {
    request.abort?.();
    return () => undefined;
  }

  const abort = () => request.abort?.();
  signal.addEventListener('abort', abort, { once: true });
  return () => signal.removeEventListener('abort', abort);
}

/**
 * Same street and building number may appear again only when the city,
 * state, or direction differs. A different building number on that street
 * is dropped.
 */
export function acceptPlaceCandidate(
  candidate: PlaceCandidate,
  seen: PlaceResultDedupe,
  globalCount: { count: number },
): boolean {
  if (isGlobalPlace(candidate) && globalCount.count >= MAX_POI) {
    return false;
  }
  if (!seen.accept(candidate)) return false;
  if (isGlobalPlace(candidate)) globalCount.count += 1;
  return true;
}

interface FetchAddressCandidatesArgs {
  text: string;
  lng?: number;
  lat?: number;
  serviceUrl?: string;
  token?: string | null;
  signal: AbortSignal;
}

/**
 * Fetch direct `/findAddressCandidates` hits for a free-text location query.
 * Runs local geocoder first (when configured), then supplements with ESRI world
 * for remaining slots. Used for the generic "location search" path so we don't
 * rely solely on `/suggest`.
 */
export async function fetchAddressCandidates({
  text,
  lng,
  lat,
  serviceUrl,
  token,
  signal,
}: FetchAddressCandidatesArgs): Promise<PlaceCandidate[]> {
  const fetchCandidates = async ({
    baseUrl,
    maxLocations,
    token: requestToken,
  }: {
    baseUrl: string;
    maxLocations: number;
    token?: string | null;
  }): Promise<PlaceCandidate[]> => {
    if (maxLocations <= 0) return [];
    try {
      const params: Record<string, string> = {
        address: text,
        outFields: '*',
        maxLocations: String(maxLocations * 2), // Request more, filter client-side
        matchOutOfRange: 'true', // Match even if house # is outside known range
        f: 'json',
      };
      if (lng != null && lat != null) params.location = `${lng},${lat}`;
      if (requestToken) params.token = requestToken;
      const res = await fetch(
        `${baseUrl}/findAddressCandidates?${new URLSearchParams(params)}`,
        { signal },
      );
      if (!res.ok) return [];
      const json = await res.json();
      const candidates = Array.isArray(json?.candidates)
        ? (json.candidates as PlaceCandidate[])
        : [];
      return candidates
        .filter(c => !!c?.address && !!c?.location)
        .map(c =>
          normalizePlaceCoords({
            ...c,
            _serviceUrl: baseUrl,
            _token: requestToken ?? null,
          }),
        );
    } catch {
      return [];
    }
  };

  const orEmpty = (lookup: Promise<PlaceCandidate[]>) =>
    lookup.catch((): PlaceCandidate[] => []);

  // Each tier only fills the slots the tiers before it left, so the place
  // list stays at MAX_POI. rsos-geocoder: RapidSOS > local > Google > Esri.
  const rsosEnabled = isRsosGeocoderEnabled();
  const tiers: ((maxLocations: number) => Promise<PlaceCandidate[]>)[] = [];
  if (rsosEnabled) {
    tiers.push(limit =>
      orEmpty(
        rsosForwardGeocode({ text, latitude: lat, longitude: lng, limit }),
      ),
    );
  }
  if (serviceUrl) {
    tiers.push(maxLocations =>
      fetchCandidates({ baseUrl: serviceUrl, maxLocations, token }),
    );
  }
  if (rsosEnabled) {
    tiers.push(limit =>
      orEmpty(
        googleForwardGeocode({ text, latitude: lat, longitude: lng, limit }),
      ),
    );
  }
  if (!isGlobalPlaceService(serviceUrl)) {
    tiers.push(maxLocations =>
      fetchCandidates({ baseUrl: ESRI_GEOCODE_SERVICE_URL, maxLocations }),
    );
  }

  const ordered: PlaceCandidate[] = [];
  for (const tier of tiers) {
    const remaining = MAX_POI - ordered.length;
    if (remaining <= 0) break;
    ordered.push(...(await tier(remaining)));
    if (signal.aborted) return [];
  }

  const seen = new PlaceResultDedupe();
  const globalCount = { count: 0 };
  const combined: PlaceCandidate[] = [];
  for (const candidate of ordered) {
    if (!acceptPlaceCandidate(candidate, seen, globalCount)) continue;
    combined.push(candidate);
  }
  return combined.slice(0, MAX_POI);
}

interface ResolveSuggestionsArgs {
  suggestions: SuggestEntry[];
  seen: PlaceResultDedupe;
  globalCount: { count: number };
  dispatch: ThunkDispatch;
  signal: AbortSignal;
}

/**
 * Resolve /suggest results into full PlaceCandidates via magicKey lookup,
 * skipping any addresses already in `seen`.
 */
export async function resolveSuggestions({
  suggestions,
  seen,
  globalCount,
  dispatch,
  signal,
}: ResolveSuggestionsArgs): Promise<PlaceCandidate[]> {
  const results = await Promise.all(
    suggestions
      .filter(s => s.magicKey)
      .map(async s => {
        let cleanupAbortListener: (() => void) | undefined;
        try {
          if (signal.aborted) return null;
          const request = dispatch(
            getAddressFromMagicKey.initiate({
              magicKey: s.magicKey,
              text: s.text,
              _serviceUrl: s._serviceUrl,
              _token: s._token,
            }),
          );
          cleanupAbortListener = abortThunkOnSignal(request, signal);
          const res = (await request) as unknown as { data?: PlaceCandidate };
          if (signal.aborted) return null;
          if (!res?.data?.location) return null;
          return normalizePlaceCoords({
            ...res.data,
            _serviceUrl: s._serviceUrl,
            _token: s._token,
          });
        } catch {
          return null;
        } finally {
          cleanupAbortListener?.();
        }
      }),
  );

  if (signal.aborted) return [];

  const resolved: PlaceCandidate[] = [];
  for (const r of results) {
    if (!r?.address || !r.location) continue;
    if (!acceptPlaceCandidate(r, seen, globalCount)) continue;
    resolved.push(r);
  }
  return resolved;
}
